import classNames from 'classnames';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FactorType, ValueMode } from '../../../types/planung-graphql-client-defs';
import { formatNumber } from '../../../utils/helper';
import { RelevantFactorTableData } from '../../Deputate/Overview/DeputateDetailsTeacherOverview';
import styles from './FactorBreakdownTooltipContent.module.scss';

type FactorBreakdownTooltipContentProps = {
  factors: Array<RelevantFactorTableData>;
  total: number;
  partialClasses?: boolean;
};

export const FactorBreakdownTooltipContent: FC<FactorBreakdownTooltipContentProps> = ({
  factors,
  total,
  partialClasses,
}) => {
  const { t } = useTranslation();

  const typeLabel = (factorType: FactorType): string => {
    switch (factorType) {
      case FactorType.Base:
        return t('factors.base');
      case FactorType.Person:
        return t('factors.person');
      case FactorType.Time:
        return t('factors.time');
      default:
        return '';
    }
  };

  const valueModeLabel = (valueMode: ValueMode): string => {
    switch (valueMode) {
      case ValueMode.Lesson:
        return t('factors.byWeek');
      case ValueMode.UnitCount:
        return t('factors.byEpoch');
      case ValueMode.UnitDurationCount:
        return t('factors.bySchoolYear');
      default:
        return '';
    }
  };

  const studentRange = (min?: number | null, max?: number | null): string | null => {
    if (min == null && max == null) return null;
    if (min != null && max != null) return `${min}–${max}`;
    if (min != null) return `≥ ${min}`;
    return `≤ ${max}`;
  };

  return (
    <div className={styles['factor-breakdown']}>
      {factors.map((factor) => {
        const usedPartial = !!partialClasses && factor.valuePartial != null;
        const value = usedPartial ? (factor.valuePartial as number) : factor.value;
        const usedRange = usedPartial
          ? studentRange(factor.studentsPartialMin, factor.studentsPartialMax)
          : studentRange(factor.studentsMin, factor.studentsMax);
        return (
          <div key={factor.uuid} className={styles.factor}>
            <div className={styles.row}>
              <span className={styles.label}>
                {typeLabel(factor.factorType)}{' '}
                <span className={styles.tag}>({usedPartial ? t('factors.divided') : t('factors.undivided')})</span>
              </span>
              <span className={styles.value}>{formatNumber(value, 4)}</span>
            </div>
            <div className={styles.detail}>
              <span className={styles.label}>{t('factors.valueMode')}</span>
              <span className={styles.value}>{valueModeLabel(factor.valueMode)}</span>
            </div>
            {usedRange && (
              <div className={styles.detail}>
                <span className={styles.label}>
                  {usedPartial ? t('factors.studentsPartial') : t('factors.studentsWhole')}
                </span>
                <span className={styles.value}>{usedRange}</span>
              </div>
            )}
            {factor.comment && <div className={styles.comment}>{factor.comment}</div>}
          </div>
        );
      })}
      <div className={classNames(styles.row, styles.total)}>
        <span className={styles.label}>{t('factors.title', { count: 1 })}</span>
        <span className={styles.value}>{formatNumber(total, 4)}</span>
      </div>
    </div>
  );
};
